import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch, getCallerAgentId, hasLiveApi } from '@/api/client'
import { agentNodeToAccount, levelToRole, type AgentNode } from '@/api/map/agent'
import { toCreateAgentRequest } from '@/api/map/create'
import * as api from '@/data/api'
import type { Account } from '@/data/types'
import { ROLE_LABEL, creatableRoles } from '@/data/types'
import { keys } from '@/api/hooks/shared/keys'

async function createAccountLive(input: api.CreateAccountInput): Promise<Account> {
  const parentId = getCallerAgentId()!
  const parentNode = await apiFetch<AgentNode>(`/v1/agents/${parentId}`)
  const parentRole = levelToRole(parentNode.level)
  const allowed = creatableRoles(parentRole)
  if (!allowed.includes(input.role)) {
    throw new Error(
      `As a ${ROLE_LABEL[parentRole]} you can only create ${allowed.map((r) => ROLE_LABEL[r]).join(' or ')} — not ${ROLE_LABEL[input.role]}`,
    )
  }

  const body = toCreateAgentRequest(input, parentId)
  const node = await apiFetch<AgentNode>('/v1/agents', {
    method: 'POST',
    headers: {
      'Idempotency-Key': crypto.randomUUID(),
    },
    body: JSON.stringify(body),
  })

  const account = agentNodeToAccount(node)
  return {
    ...account,
    username: input.username.trim() || account.username,
    displayName: input.displayName.trim() || account.displayName,
    commission: input.commission,
    betLimits: input.betLimits,
    games: input.games,
    comments: input.comments,
    flags: ['no-login-yet'],
  }
}

async function createAccount(input: api.CreateAccountInput): Promise<Account> {
  if (hasLiveApi()) return createAccountLive(input)
  return api.createAccount(input)
}

export function useCreateAccount() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: createAccount,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['downline'] })
      client.invalidateQueries({ queryKey: ['children'] })
      client.invalidateQueries({ queryKey: ['overview'] })
      client.invalidateQueries({ queryKey: keys.session })
      client.invalidateQueries({ queryKey: keys.createParent })
    },
  })
}
